import { transpose, getNewKey } from './transpose';
import { replaceChords } from './chordPatterns';
import { mapChartDataChords } from './chartData';
import { normalizeKeyDisplay } from './diatonicChords';
import { getSetting, settingKeys } from './settings';
import scales from '../data/scales.json';

const { MAJOR_SCALE_KEYS, MINOR_SCALE_KEYS } = scales;

/**
 * Get all available keys for transposition with normalized display based on user preference
 * @param {string} originalKey - The original song key to determine if major/minor
 * @returns {Array} Array of objects with _id and name for dropdown
 */
export function getTransposeKeys(originalKey) {
  // Determine if original key is minor (contains 'm')
  const isMinor = originalKey && originalKey.includes('m');

  // Select appropriate scale (major or minor)
  const keysToShow = isMinor ? MINOR_SCALE_KEYS : MAJOR_SCALE_KEYS;

  // Get user preference for sharp/flat notation
  const preferSharps = getSetting(settingKeys.preferSharps) !== false; // Default to true

  return keysToShow.map((key) => {
    const displayName = normalizeKeyDisplay(key, preferSharps);

    // Mark the current key, and show how far each other key is from it
    let name = displayName;
    if (key === originalKey) {
      name = `${displayName} (Current Key)`;
    } else {
      const semitones = calculateSemitones(originalKey, key);
      if (semitones) {
        name = `${displayName} (${semitones > 0 ? '+' : ''}${semitones})`;
      }
    }

    return {
      _id: key,
      name: name,
    };
  });
}

/**
 * Calculate semitones between two keys
 * @param {string} fromKey - Original key (e.g., 'C', 'Fm')
 * @param {string} toKey - Target key
 * @returns {number} Semitones difference (-11 to +11), or 0 if keys are same
 */
export function calculateSemitones(fromKey, toKey) {
  if (!fromKey || !toKey || fromKey === toKey) return 0;

  // Determine which scale to use
  let scale = MAJOR_SCALE_KEYS;
  let fromIndex = scale.indexOf(fromKey);

  // Check if it's a minor key
  if (fromIndex === -1) {
    scale = MINOR_SCALE_KEYS;
    fromIndex = scale.indexOf(fromKey);
  }

  // If key not found, return 0
  if (fromIndex === -1) return 0;

  // Find target key in the same scale
  const toIndex = scale.indexOf(toKey);
  if (toIndex === -1) return 0;

  // Calculate semitone difference
  let semitones = toIndex - fromIndex;

  // Normalize to -11 to +11 range (closest distance on chromatic circle)
  if (semitones > 6) {
    semitones = semitones - 12;
  } else if (semitones < -6) {
    semitones = semitones + 12;
  }

  return semitones;
}

/**
 * Calculate the resulting key after transposing by semitones
 * @param {string} originalKey - Original key (e.g., 'C', 'Fm')
 * @param {number} semitones - Number of semitones to transpose
 * @returns {string} The resulting key
 */
export function getKeyAfterTranspose(originalKey, semitones) {
  if (!originalKey || semitones === 0) return originalKey;

  let currentKey = originalKey;
  const direction = semitones > 0 ? 'up' : 'down';
  const steps = Math.abs(semitones);

  // Transpose the key step by step
  for (let i = 0; i < steps; i++) {
    currentKey = getNewKey(currentKey, direction);
  }

  return currentKey;
}

/**
 * Transpose all chords in a text by semitones
 * @param {string} text - Lyrics/chords text
 * @param {string} originalKey - Original song key
 * @param {number} semitones - Number of semitones to transpose
 * @returns {string} Transposed text
 */
export function transposeLyrics(text, originalKey, semitones) {
  if (!text || !originalKey || semitones === 0) return text;

  const direction = semitones > 0 ? 'up' : 'down';
  const steps = Math.abs(semitones);

  let transposedText = text;
  let keyAtStep = originalKey;

  // Transpose step by step to maintain correct key context
  for (let i = 0; i < steps; i++) {
    const currentKeyForReplace = keyAtStep; // Capture key for this iteration
    transposedText = replaceChords(transposedText, (chord) => {
      return transpose(chord, currentKeyForReplace, 1, direction);
    });
    keyAtStep = getNewKey(keyAtStep, direction);
  }

  return transposedText;
}

/**
 * Transpose an entire song (all parts)
 *
 * A part that carries a key of its own is moved by the same interval as everything else — the
 * modulation is the relationship between the part and the song, and transposing the song doesn't
 * change it: a bridge a fourth below stays a fourth below. Its key has to travel with its chords
 * or it ends up naming a key the chords underneath it are no longer in, which is the same trap
 * midiJson.js keeps `sourceKey` for. The song's own key is rewritten by the callers in View.jsx,
 * which keep `originalKey` beside it for the same reason.
 *
 * @param {Object} song - Song object with parts array
 * @param {number} semitones - Number of semitones to transpose
 * @returns {Object} Song with transposed parts (creates deep copy)
 */
export function transposeSong(song, semitones) {
  if (!song || !song.parts || semitones === 0) return song;

  // Deep clone to avoid mutation
  const transposedSong = JSON.parse(JSON.stringify(song));

  const transposePart = (part) => ({
    ...part,
    // The part's own key, where it has one: it decides this part's sharps and flats, which is the
    // whole of what transposeLyrics reads a key for. Spelled out rather than calling partKey.js's
    // getPartKey, which reads the same — that module imports getKeyAfterTranspose from here, and
    // importing back would close the cycle.
    lyrics: transposeLyrics(part.lyrics, part.key || song.key, semitones),
    // The part's chart moves with its lyrics
    ...(part.chartData && {
      chartData: mapChartDataChords(part.chartData, (text) => transposeLyrics(text, part.key || song.key, semitones)),
    }),
    ...(part.key && { key: getKeyAfterTranspose(part.key, semitones) }),
  });

  transposedSong.parts = transposedSong.parts.map(transposePart);

  // The parts as written, which View keeps beside the stripped ones while Hide Chords is on. They
  // are what the page is put back to when chords come back, and what the chart player reads the
  // words off meanwhile — left behind here, both would return in the key the song was in before.
  if (transposedSong.originalParts) transposedSong.originalParts = transposedSong.originalParts.map(transposePart);

  return transposedSong;
}

/**
 * Get the transpose target key from semitones value
 * @param {string} originalKey - Original song key
 * @param {number|string} transposeBy - Semitones value from form
 * @returns {string} Target key name for display, or empty string
 */
export function getTransposeTargetKey(originalKey, transposeBy) {
  const semitones = typeof transposeBy === 'string' ? parseInt(transposeBy) : transposeBy;

  if (!originalKey || !semitones || isNaN(semitones)) return '';

  return getKeyAfterTranspose(originalKey, semitones);
}
